// pages/affiliatePartners/bulk-promo-codes.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FaRegCopy } from "react-icons/fa";
import Loader from "../../components/Loader";
import { app_url } from "../../config";

/* ---------- Helper functions ---------- */

const getStatus = (code) => {
  const s = String(code.status || "").toUpperCase();

  if (s === "USED" || code.used_by_email || code.used_by_first_name) {
    return { label: "Used", key: "used" };
  }

  if (s === "EXPIRED") {
    return { label: "Expired", key: "expired" };
  }

  if (s === "INACTIVE" || code.isActive === 0) {
    return { label: "Inactive", key: "inactive" };
  }

  return { label: "Active", key: "active" };
};

// API returns expires_at already formatted in the expiry timezone.
const splitExpiry = (value) => {
  if (!value) return { date: "--", time: "" };

  const [date, ...rest] = String(value).split(", ");

  return { date, time: rest.join(", ") };
};

const fullName = (first, last) => [first, last].filter(Boolean).join(" ");

/* ---------- Styles ---------- */

const styles = `
.bpc-shell {
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16,24,40,.04);
}

.bpc-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eef0f4;
}

.bpc-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2433;
  margin: 0;
}

.bpc-sub {
  font-size: 13px;
  color: #6b7385;
  margin: 2px 0 0;
}

.bpc-search {
  position: relative;
  width: 260px;
  max-width: 100%;
}

.bpc-search input {
  width: 100%;
  padding: 9px 12px 9px 36px;
  border: 1px solid #dfe3ea;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  background: #fafbfc;
}

.bpc-search input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99,102,241,.15);
  background: #fff;
}

.bpc-search svg {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9aa1b2;
}

.bpc-table {
  width: 100%;
  min-width: 1000px;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
}

.bpc-table th,
.bpc-table td {
  white-space: nowrap;
}

.bpc-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f7f8fb;
  color: #6b7385;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .02em;
  text-transform: none;
  padding: 12px 20px;
  border-bottom: 1px solid #eef0f4;
}

.bpc-table tbody td {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f2f6;
  vertical-align: middle;
  font-size: 14px;
  color: #2b3345;
}

.bpc-table tbody tr {
  transition: background .12s;
}

.bpc-table tbody tr:hover {
  background: #f9faff;
}

.bpc-table tbody tr:last-child td {
  border-bottom: none;
}

.bpc-idx {
  color: #9aa1b2;
  font-variant-numeric: tabular-nums;
  width: 48px;
}

.bpc-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-weight: 600;
  letter-spacing: .04em;
  color: #1d2433;
  background: #f1f2f9;
  padding: 4px 10px;
  border-radius: 8px;
  display: inline-block;
}

.bpc-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.bpc-pill::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.bpc-pill.active {
  background: #e8f7ee;
  color: #15803d;
}

.bpc-pill.used {
  background: #eef0ff;
  color: #4f46e5;
}

.bpc-pill.expired {
  background: #fdecec;
  color: #b42318;
}

.bpc-pill.inactive {
  background: #eff1f5;
  color: #667085;
}

.bpc-discount {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.bpc-muted {
  color: #8a92a6;
  font-size: 12px;
}

.bpc-copy {
  border: 1px solid #dfe3ea;
  background: #fff;
  color: #4b5468;
  border-radius: 8px;
  padding: 6px 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  transition: all .15s;
  cursor: pointer;
}

.bpc-copy:hover {
  border-color: #6366f1;
  color: #4f46e5;
  background: #f5f5ff;
}

.bpc-empty {
  text-align: center;
  padding: 56px 20px;
  color: #6b7385;
}

.bpc-empty b {
  display: block;
  color: #1d2433;
  font-size: 15px;
  margin-bottom: 4px;
}

.bpc-scroll {
  overflow: auto;
}

.bpc-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.bpc-scroll::-webkit-scrollbar-thumb {
  background: #d5d9e2;
  border-radius: 8px;
}

.bpc-scroll::-webkit-scrollbar-thumb:hover {
  background: #b9bfcc;
}

@media (max-width: 768px) {
  .bpc-search {
    width: 100%;
  }
}
`;

/* ---------- Main component ---------- */

const BulkPromoCodes = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const affiliatePartnerId = searchParams.get("affiliate_partner_id");
  const generationId = searchParams.get("generation_id");
  const navigate = useNavigate();

  const token = JSON.parse(localStorage.getItem("adminEtijwaal"))?.token;

  const [codes, setCodes] = useState([]);
  const [batch, setBatch] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!affiliatePartnerId || !generationId) {
      setLoading(false);
      setCodes([]);
      return;
    }

    let cancelled = false;

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const load = async () => {
      setLoading(true);

      try {
        const res = await axios.get(
          `${app_url}/api/affiliatePartner/get_single_use_discount_codes_batches_for_aff_partner?affiliate_partner_id=${affiliatePartnerId}&generation_id=${generationId}`,
          { headers },
        );

        if (cancelled) return;

        setCodes(res?.data?.data);
      } catch (err) {
        if (cancelled) return;

        setCodes([]);

        if (err.response?.status === 401) {
          toast.error(err.response.data?.message || "Not authorized");
          navigate("/");
        } else if (err.response) {
          toast.error(err.response.data?.message || "Something went wrong.");
        } else {
          toast.error("Network error. Please check your internet connection.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [affiliatePartnerId, generationId, token, navigate]);

  // Copy promo code to clipboard.
  const copyCode = (code) => {
    navigator.clipboard
      .writeText(code)
      .then(() => toast.success(`Copied ${code}`))
      .catch(() => toast.error("Could not copy"));
  };

  // Search all promo codes. No pagination or slicing.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return codes;

    return codes.filter(
      (c) =>
        String(c.promoCode || "")
          .toLowerCase()
          .includes(q) ||
        String(c.used_by_email || "")
          .toLowerCase()
          .includes(q),
    );
  }, [codes, search]);

  return (
    <section className="wrapper overflow-hidden">
      <style>{styles}</style>

      <div className="d-flex justify-content-between align-items-center w-100 mb-3">
        <p className="heading mb-0">
          <i
            className="bi bi-arrow-left-circle-fill fs-3 me-3 my-auto pointer"
            onClick={() => navigate(-1)}
          />
          Generated Discount Codes
        </p>
      </div>

      <div className="bpc-shell">
        {/* Toolbar */}
        <div className="bpc-toolbar">
          <div>
            <h6 className="bpc-title">
              {batch?.promoCode ? `Batch ${batch.promoCode}` : "Batch codes"}
            </h6>

            <p className="bpc-sub">
              {codes.length} code{codes.length === 1 ? "" : "s"}
              {batch?.package_name ? ` · ${batch.package_name}` : ""}
            </p>
          </div>

          {/* Search */}
          <div className="bpc-search">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>

            <input
              type="search"
              placeholder="Search by code or email"
              aria-label="Search by code or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-5">
            <div className="parent-loader">
              <Loader />
            </div>
          </div>
        ) : !generationId ? (
          <div className="bpc-empty">
            <b>No batch selected</b>
            Open a batch from the partner details page to see its codes.
          </div>
        ) : filtered.length === 0 ? (
          <div className="bpc-empty">
            <b>No promo codes found</b>
            {search
              ? "Try a different search term."
              : "This batch has no codes."}
          </div>
        ) : (
          <div className="bpc-scroll">
            <table className="bpc-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Promo code</th>
                  <th>Status</th>
                  <th>Discount</th>
                  <th>Commission</th>
                  <th>Expires</th>
                  <th>Used by</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((code, i) => {
                  const status = getStatus(code);
                  const expiry = splitExpiry(code.expires_at);

                  const usedBy = fullName(
                    code.used_by_first_name,
                    code.used_by_last_name,
                  );

                  return (
                    <tr key={code.id}>
                      <td className="bpc-idx">{i + 1}</td>

                      <td>
                        <span className="bpc-code">{code.promoCode}</span>
                      </td>

                      <td>
                        <span className={`bpc-pill ${status.key}`}>
                          {status.label}
                        </span>
                      </td>

                      <td className="bpc-discount">
                        {code.discount_percentage != null
                          ? `${code.discount_percentage}%`
                          : "--"}
                      </td>

                      <td className="bpc-discount">
                        {code.commision_rate != null
                          ? `${code.commision_rate}%`
                          : "--"}
                      </td>

                      <td>
                        <div>{expiry.date}</div>
                        <div className="bpc-muted">
                          {expiry.time}
                          {code.expiry_timezone
                            ? ` · ${code.expiry_timezone}`
                            : ""}
                        </div>
                      </td>

                      <td>
                        {usedBy || code.used_by_email ? (
                          <>
                            <div>{usedBy || "--"}</div>
                            <div className="bpc-muted">
                              {code.used_by_email || ""}
                            </div>
                          </>
                        ) : (
                          <span className="bpc-muted">Not used</span>
                        )}
                      </td>

                      <td>
                        <div>{new Date(code.createdAt).toLocaleString()}</div>
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <button
                          type="button"
                          className="bpc-copy"
                          onClick={() => copyCode(code.promoCode)}
                          aria-label={`Copy ${code.promoCode}`}
                        >
                          <FaRegCopy size={13} /> Copy
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default BulkPromoCodes;
